/**
 * useHoverDismiss — 마우스 hover 툴팁을 터치 환경에서 정리하는 훅.
 *
 * 터치 기기는 탭이 합성 mousemove 로 들어와 hover 상태가 켜지지만 mouseleave 가 오지 않는다.
 * 그래서 스크롤로 화면을 이동해도 position:fixed 툴팁이 옛 좌표에 떠 있게 된다.
 * → hover 가 켜진 동안 window scroll(캡처) · 바깥 touchstart/pointerdown 을 감시해 닫는다.
 *
 * 사용: useHoverDismiss(hover != null, () => setHover(null), svgRef);
 *   - hostRef 를 주면 그 요소 안에서 시작한 터치는 무시(차트 안 탭은 새 hover 로 갱신됨).
 */
import { useEffect, useRef, type RefObject } from "react";

export function useHoverDismiss(
  active: boolean,
  dismiss: () => void,
  hostRef?: RefObject<Element | null>,
) {
  const dismissRef = useRef(dismiss);
  useEffect(() => {
    dismissRef.current = dismiss;
  }, [dismiss]);
  useEffect(() => {
    if (!active) return;
    const dismiss = () => dismissRef.current();
    const onScroll = () => dismiss();
    const onDown = (e: Event) => {
      const host = hostRef?.current;
      if (host && e.target instanceof Node && host.contains(e.target)) return;
      dismiss();
    };
    window.addEventListener("scroll", onScroll, { capture: true, passive: true });
    document.addEventListener("touchstart", onDown, { capture: true, passive: true });
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("touchstart", onDown, true);
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, [active, hostRef]);
}
